import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { mergeTests } from '../../test-utilities/mergeTests';
import { Intent } from '../utilities';
import { useHasFeatureFlag } from '../../../selectors/useHasFeatureFlag';
import { IHasTestData } from '../../test-utilities/IHasTestData';

export interface IButtonProps {
  /** Ghost intent still commands the same space as a button, isInline is used when you don't want that space */
  /** Ghost intent and isInline should be used when button should look like link */
  isInline?: boolean;

  /** intent now used in place of intent + appearance property */
  intent: Intent;

  /** Use Spruce color scheme instead of default theme colors */
  $useSpruceColors?: boolean;

  // TODO: add button handling of leading/trailing icons
}

const primaryStyles = css<IButtonProps>`
  color: ${(props) => props.theme.colors.white};
  background: ${(props) => (props.$useSpruceColors ? props.theme.colors.spruceDarkGreen : props.theme.colors.primary)};

  :hover {
    background: ${(props) =>
      props.$useSpruceColors ? props.theme.colors.spruceDarkGreenHover : props.theme.colors.primaryDarkest};
  }
`;

const secondaryStyles = css<IButtonProps>`
  color: ${(props) =>
    props.$useSpruceColors ? props.theme.colors.spruceDarkGreen : props.theme.colors.defaultContentText};
  background: ${(props) => props.theme.colors.gray50};
  border: ${(props) =>
    props.$useSpruceColors
      ? `1px solid ${props.theme.colors.spruceDarkGreen}`
      : `1px solid ${props.theme.colors.gray400}`};
  box-sizing: border-box;

  :hover {
    color: ${(props) =>
      props.$useSpruceColors ? props.theme.colors.spruceDarkGreen : props.theme.colors.defaultContentText};
    background: ${(props) => props.theme.colors.defaultContentBackground};
    border: ${(props) =>
      props.$useSpruceColors
        ? `1px solid ${props.theme.colors.spruceDarkGreen}`
        : `1px solid ${props.theme.colors.primary}`};
  }

  :active {
    background: ${(props) =>
      props.$useSpruceColors ? props.theme.colors.spruceDarkGreen : props.theme.colors.primaryDark};
    color: ${(props) => props.theme.colors.white};
  }
`;

const ghostStyles = css`
  color: ${(props) => props.theme.colors.black};
  background: ${(props) => props.theme.colors.white};
  box-shadow: none;

  :hover {
    color: ${(props) => props.theme.colors.white};
    background: ${(props) => props.theme.colors.primaryLightest};
  }

  :active {
    color: ${(props) => props.theme.colors.primary};
    background: ${(props) => props.theme.colors.gray200};
  }

  :disabled {
    cursor: not-allowed;
    color: ${(props) => props.theme.colors.gray};
    background: ${(props) => props.theme.colors.white};

    &:hover {
      box-shadow: none;
    }
  }
`;

const dangerStyles = css`
  color: ${(props) => props.theme.colors.white};
  background: ${(props) => props.theme.colors.error};

  :hover {
    background: ${(props) => props.theme.colors.errorText};
  }

  :active {
    background: ${(props) => props.theme.colors.errorDarkest};
  }
`;

const intent = {
  primary: css`
    ${primaryStyles}
  `,
  secondary: css`
    ${secondaryStyles}
  `,
  ghost: css`
    ${ghostStyles}
  `,
  danger: css`
    ${dangerStyles}
  `,
};

const focusStyles = css<IButtonProps>`
  outline: none;

  &:active {
    box-shadow: ${(props) =>
      `0px 1px 2px ${props.theme.colors.focusHoverShadow}, 0px 0px 0px 2px ${props.theme.colors.white}, 0px 0px 0px 4px ${props.theme.colors.primary}`};
  }
`;
const inlinePadding = css`
  padding: 0;
`;

const disabledStyle = css`
  :disabled {
    border: none;
    cursor: not-allowed;
    color: ${(props) => props.theme.colors.darkGray};
    background: ${(props) => props.theme.colors.lightGray};

    &:hover {
      box-shadow: ${(props) => `0px 1px 2px ${props.theme.colors.focusHoverShadow}`};
    }
  }
`;

const buttonLikeALinkStyles = css`
  background: none;

  :hover {
    color: ${(props) => props.theme.colors.black};
    background: none;
    box-shadow: none;
    text-decoration: underline;
  }
`;

export const ButtonCss = css<IButtonProps>`
  border: none;
  border-radius: 100px;
  cursor: pointer;
  padding: ${(props) => props.theme.spacing.spacing4} ${(props) => props.theme.spacing.defaultButtonPadding};
  ${(props) => (props.isInline ? inlinePadding : ``)};
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);

  :focus {
    ${(props) => (props.isInline ? `` : focusStyles)}
  }

  :hover {
    box-shadow: unset;
  }

  ${(props) => intent[props.intent]};

  ${(props) =>
    props.isInline && props.intent === 'ghost'
      ? buttonLikeALinkStyles
      : ``} /** ghost button has different disabled style */ ${(props) =>
    props.intent === 'ghost' ? `` : disabledStyle}

  /** Overrides needed for react.minipieces */
  min-width: 0;
`;

const StyledButton = styled.button.attrs(
  (
    props: IButtonProps &
      IHasTestData &
      React.ButtonHTMLAttributes<HTMLButtonElement> & {
        children?: string | ReactNode;
        as?: React.ElementType;
      }
  ) => ({
    type: props.type || 'button',
    'data-test-class': mergeTests(props['data-test-class'] || 'button', props.type || 'button', props.intent),
    'data-test-id': mergeTests(
      props['data-test-id'] || 'button',
      props.type || 'button',
      typeof props.children === 'string' ? props.children : undefined
    ),
  })
)<IButtonProps>`
  ${ButtonCss}
`;

export const Button = React.forwardRef<
  HTMLButtonElement,
  IButtonProps &
    IHasTestData &
    React.ButtonHTMLAttributes<HTMLButtonElement> & {
      children?: string | ReactNode;
      as?: React.ElementType;
    }
>((props, ref) => {
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const resolvedUseSpruceColors = props.$useSpruceColors ?? !isZillowClosingEnabled;
  return <StyledButton {...props} ref={ref} $useSpruceColors={resolvedUseSpruceColors} />;
});
